import type { HomeDto, RotaPatternResponse } from '@lmc/ts-common'
import { postJSON } from 'common/services/api'
import { normalizeErrorMessage, showToast } from 'common/utils'
import dayjs from 'dayjs'
import { useMutation, useQueryClient } from 'react-query'
import { useSelector } from 'react-redux'
import {
  getRotaPatternSubmitValues,
  type RotaPatternFormValues,
  type RotaPatternModalType
} from '../RotaPattern/utils'
import {
  CHECK_ROTA_GENERATED_QUERY_KEY,
  REVISIONS_QUERY_KEY,
  ROTA_GENERATED_QUERY_KEY,
  ROTA_PATTERN_CREATE_QUERY_KEY,
  ROTA_PATTERN_QUERY_KEY,
  ROTA_PATTERN_URL,
  SHIFT_TYPES_QUERY_KEY,
  VISIT_TYPES_QUERY_KEY
} from './queryConstants'

type UseRotaPatternCreateProps = {
  type?: RotaPatternModalType
  onCreateSuccess?: () => void
}

type UseRotaPatternCreateReturn = {
  createPattern: (values: RotaPatternFormValues) => Promise<RotaPatternResponse>
  isLoadingCreate?: boolean
}

export const useRotaPatternCreate = ({
  type
}: UseRotaPatternCreateProps): UseRotaPatternCreateReturn => {
  const queryClient = useQueryClient()

  const handleError = (err: unknown) => {
    showToast({
      title: normalizeErrorMessage(err),
      type: 'error'
    })
  }

  const handleSuccess = (message: string) => {
    showToast({
      title: message,
      type: 'success'
    })
  }

  const home = useSelector(
    (state: { global: { home: HomeDto } }) => state.global.home
  )

  // CREATE PATTERN
  const createFunction = async (
    values: RotaPatternFormValues
  ): Promise<RotaPatternResponse> => {
    const patterFinalSubmitValues = getRotaPatternSubmitValues({
      type,
      home,
      values
    })

    return postJSON({
      url: `${ROTA_PATTERN_URL}`,
      method: 'POST',
      json: patterFinalSubmitValues,
      isRota: true
    })
  }

  const { mutateAsync: createMutation, isLoading: isLoadingCreate } =
    useMutation<RotaPatternResponse, unknown, RotaPatternFormValues, unknown>(
      [ROTA_PATTERN_CREATE_QUERY_KEY],
      createFunction,
      {
        onSuccess: (res, variables) => {
          if (res?.title) {
            handleSuccess('Pattern created successfully')

            queryClient.invalidateQueries(ROTA_PATTERN_QUERY_KEY)
            queryClient.invalidateQueries([VISIT_TYPES_QUERY_KEY])
            queryClient.invalidateQueries([SHIFT_TYPES_QUERY_KEY])
            queryClient.invalidateQueries([ROTA_GENERATED_QUERY_KEY])

            // Invalidate the check generated rota using the pattern's start date
            if (variables?.patternStart) {
              queryClient.invalidateQueries([
                CHECK_ROTA_GENERATED_QUERY_KEY,
                dayjs(variables.patternStart).toDate()
              ])
            }
          }
        },
        onError: (err) => handleError(err)
      }
    )

  return {
    createPattern: createMutation,
    isLoadingCreate
  }
}
